import React from 'react'
import { X, Clock, MapPin, Users, Calendar, ExternalLink, FileText } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { CalendarEvent } from '@/hooks/useCalendarEvents'

interface EventDetailModalProps {
  event: CalendarEvent | null
  isOpen: boolean
  onClose: () => void
}

const EventDetailModal: React.FC<EventDetailModalProps> = ({
  event,
  isOpen,
  onClose
}) => {
  if (!isOpen || !event) return null

  const formatDateTime = (dateString: string) => {
    try {
      const date = parseISO(dateString)
      return format(date, "EEEE, d 'de' MMMM 'de' yyyy 'as' HH:mm", { locale: ptBR })
    } catch {
      return dateString
    }
  }

  const formatTime = (dateString: string) => {
    try {
      const date = parseISO(dateString)
      return format(date, 'HH:mm', { locale: ptBR })
    } catch {
      return ''
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm dark:bg-black/60"
        onClick={onClose}
      />

      <div className="relative bg-white border border-border rounded-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden shadow-md dark:bg-[#27272A] dark:border-[#3F3F46] dark:rounded-xl dark:shadow-none">
        <div className="flex items-center justify-between p-4 border-b border-border dark:border-neutral-800">
          <h2 className="text-lg font-semibold text-[#0F172A] dark:text-neutral-50">
            Detalhes do Evento
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#F1F5F9] rounded-xl transition-colors duration-200 dark:hover:bg-neutral-800 dark:rounded-lg"
          >
            <X className="w-5 h-5 text-muted-foreground dark:text-neutral-400" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          <h3 className="text-xl font-semibold text-[#0F172A] mb-4 dark:text-neutral-50">
            {event.title || 'Sem titulo'}
          </h3>

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-[#2563EB] mt-0.5 dark:text-[#60A5FA]" />
              <div>
                <p className="text-sm font-medium text-foreground dark:text-neutral-300">Data e Hora</p>
                <p className="text-sm text-[#64748B] capitalize dark:text-neutral-400">
                  {formatDateTime(event.start)}
                </p>
                {event.end && (
                  <p className="text-sm text-[#64748B] dark:text-neutral-500">
                    Termina as {formatTime(event.end)}
                  </p>
                )}
              </div>
            </div>

            {event.location && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#2563EB] mt-0.5 dark:text-[#60A5FA]" />
                <div>
                  <p className="text-sm font-medium text-foreground dark:text-neutral-300">Local</p>
                  <p className="text-sm text-[#64748B] dark:text-neutral-400">{event.location}</p>
                </div>
              </div>
            )}

            {event.description && (
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-[#2563EB] mt-0.5 dark:text-[#60A5FA]" />
                <div>
                  <p className="text-sm font-medium text-foreground dark:text-neutral-300">Descricao</p>
                  <p className="text-sm text-[#64748B] whitespace-pre-wrap dark:text-neutral-400">
                    {event.description}
                  </p>
                </div>
              </div>
            )}

            {event.attendees && event.attendees.length > 0 && (
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-[#2563EB] mt-0.5 dark:text-[#60A5FA]" />
                <div>
                  <p className="text-sm font-medium text-foreground dark:text-neutral-300">
                    Participantes ({event.attendees.length})
                  </p>
                  <div className="mt-1 space-y-1">
                    {event.attendees.map((attendee, idx) => (
                      <p key={idx} className="text-sm text-[#64748B] dark:text-neutral-400">
                        {attendee}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-border flex justify-end gap-3 dark:border-neutral-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-[#64748B] hover:text-foreground hover:bg-[#F1F5F9] rounded-xl transition-colors duration-200 dark:text-neutral-400 dark:hover:text-neutral-300 dark:hover:bg-neutral-800 dark:rounded-lg"
          >
            Fechar
          </button>
          {event.html_link && (
            <a
              href={event.html_link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 text-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-[12px] shadow-sm transition-colors duration-200"
            >
              <ExternalLink className="w-4 h-4" />
              Abrir no Google Calendar
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

export default EventDetailModal
